import { useRef, useState } from "react"
import { INSCRIPTION_FUNNEL_LOST } from "../constants/inscriptionContent"
import { useInscriptionFunnel } from "../context/InscriptionFunnelContext"
import {
    answersRefusalMessage,
    inscriptionFunnelService,
} from "../services/inscriptionFunnelService"
import { quoteIsWithoutCommitment } from "../utils/formulaChoice"
import { useInscriptionLead } from "./useInscriptionLead"

const SUPERSEDED = Symbol("superseded")

const formulaOf = (state) => ({
    offerType: state.offerType,
    weeklyHours: state.weeklyHours,
    hoursAllocation: state.hoursAllocation,
    withCommitment: state.withCommitment,
    committedFormula: state.committedFormula ?? null,
})

/**
 * Écrit un choix de formule fait HORS du compositeur — la page du format : le
 * parcours le retient tout de suite, le serveur renvoie le devis.
 *
 * Le compositeur garde son propre écrivain : il regroupe des rafales de clics
 * sur des réponses qu'il tient en local. Ici un geste est un choix entier
 * (`{ offerType, weeklyHours, hoursAllocation, withCommitment,
 * committedFormula }`, tel que le rendent `pickFormat` et `toggleCommitment`),
 * écrit tel quel.
 *
 * Le parcours ne garde jamais un choix que le serveur n'a pas : un
 * enregistrement qui échoue le ramène à la dernière formule chiffrée, et son
 * devis reste à redemander (`quoteStale`) — un enregistrement parti juste
 * avant a pu passer. Renvoyer le choix affiché le redemande.
 */
export const useFormulaChoiceSave = () => {
    const { state, update } = useInscriptionFunnel()
    const { funnelStateId } = useInscriptionLead()
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    // Seule la réponse du DERNIER choix a le droit d'écrire le devis.
    const sequence = useRef(0)
    const lastPriced = useRef(null)
    // Un enregistrement à la fois : deux requêtes parties ensemble peuvent
    // atteindre le serveur dans le désordre et y laisser l'avant-dernier
    // choix. Celui qui attend son tour ne part que s'il est encore le dernier.
    const queue = useRef(Promise.resolve())

    const save = (choice) => {
        if (!funnelStateId) {
            setError(INSCRIPTION_FUNNEL_LOST)
            return
        }

        const { committedFormula, ...answers } = choice
        if (!state.quoteStale) lastPriced.current = formulaOf(state)
        update({ ...answers, committedFormula, quoteStale: true })

        const current = ++sequence.current
        const isLatest = () => current === sequence.current
        setSaving(true)
        queue.current = queue.current
            .then(() =>
                isLatest()
                    ? inscriptionFunnelService.updateInscriptionAnswers(
                          { stateId: funnelStateId },
                          answers
                      )
                    : SUPERSEDED
            )
            .then((res) => {
                if (res === SUPERSEDED || !isLatest()) return
                setError(null)
                const quote = res?.data?.data?.quote ?? null
                // L'engagement vendu est celui du devis : s'il n'a pas suivi
                // la demande, le parcours se recale sur lui.
                const sold = !quoteIsWithoutCommitment(quote)
                update({
                    quote,
                    quoteStale: false,
                    ...(quote && sold !== answers.withCommitment
                        ? { withCommitment: sold, committedFormula: null }
                        : {}),
                })
            })
            // Un refus se LIT : le parent doit savoir quoi corriger.
            .catch((err) => {
                if (!isLatest()) return
                if (lastPriced.current) update(lastPriced.current)
                setError(
                    answersRefusalMessage(
                        err,
                        "Votre choix n'a pas pu être enregistré."
                    )
                )
            })
            .finally(() => {
                if (isLatest()) setSaving(false)
            })
    }

    return { save, saving, error }
}
